import dynamic from 'next/dynamic';
import { useRef, useState } from 'react';
import Button from 'components/Button';
import SeoPanel from './SeoPanel';
import SuggestedLink from './SuggestedLink';
import FaqOfferFields from './FaqOfferFields';
import StorySponsorFields from './StorySponsorFields';
import LeadImageFields from './LeadImageFields';
import DirectoryTopicsPicker from './DirectoryTopicsPicker';
import { STORY_LINK_CONFIG, formatDateTime } from 'utils/newsroom';
import { storySponsorHosts } from 'utils/story-links';
import { storyLinkRelPlugin } from './storyLinkRel';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';

const Editor = dynamic(() => import('components/Editor'), { ssr: false });

const StoryBodyEditor = ({ alert, draft, onChange }) => {
    const sponsorHosts = useRef([]);
    sponsorHosts.current = storySponsorHosts(draft, alert);
    // CKEditor reads its config once, so the plugin reads the hosts through the ref.
    const [config] = useState(() => ({
        extraPlugins: [storyLinkRelPlugin(() => sponsorHosts.current)],
    }));
    return (
        <Editor
            data={draft.body}
            setData={(value) => onChange('body', value)}
            height={'420px'}
            link={STORY_LINK_CONFIG}
            config={config}
        />
    );
};

// The Storify template: the news article that sits on top of the raw alert.
const StoryEditor = ({
    alert,
    researchOn,
    story,
    draft,
    authors,
    slug,
    published,
    dirty,
    saving,
    uploadingLeadImage,
    error,
    onChange,
    onSponsorChange,
    onSave,
    onLeadImage,
    onClose,
}) => (
    <section className={styles.editor} aria-label="Story editor">
        <div className={styles.editorIntro}>
            <span className={styles.kicker}>Storify</span>
            <p>
                The story sits above the raw alert, at this URL. Nothing changes
                on the public page until you press Publish.
            </p>
            {story?.lastEditedByName && story?.lastEditedAt && (
                <p>
                    Last edited by {story.lastEditedByName},{' '}
                    {formatDateTime(story.lastEditedAt)}
                </p>
            )}
        </div>

        <div className={styles.editorGrid}>
            <div className={styles.editorMain}>
                <label className={styles.label} htmlFor="story-headline">
                    Headline (H1)
                </label>
                <input
                    id="story-headline"
                    className={[styles.input, styles.headlineInput].join(' ')}
                    value={draft.headline}
                    maxLength={255}
                    onChange={(e) => onChange('headline', e.target.value)}
                />

                <label className={styles.label} htmlFor="story-byline">
                    Byline
                </label>
                <select
                    id="story-byline"
                    className={styles.input}
                    value={draft.bylineAuthorId}
                    onChange={(e) => {
                        const id = e.target.value;
                        const author = authors.find((a) => String(a.id) === id);
                        onChange('bylineAuthorId', id);
                        onChange('byline', author?.name ?? '');
                    }}
                >
                    <option value="">None: shows as Newspage</option>
                    {authors.map((author) => (
                        <option key={author.id} value={String(author.id)}>
                            {author.name}
                        </option>
                    ))}
                </select>
                <p className={styles.hint}>
                    Photo, role, bio and LinkedIn are edited on the author in
                    the CMS.
                </p>

                <LeadImageFields
                    story={story}
                    draft={draft}
                    uploading={uploadingLeadImage}
                    onChange={onChange}
                    onUpload={onLeadImage}
                />

                <span className={styles.label}>Story</span>
                <p className={styles.hint}>
                    Use Heading 2 for subheadings and Heading 3 for FAQ
                    questions, and link to sources and to the experts&rsquo;
                    Newspages. Links to official sources (gov.uk, ONS, Bank of
                    England, FCA) are followed, others are nofollow; the
                    &ldquo;Follow link&rdquo; switch on each link overrides
                    that. Quotes from the responses below can be fixed for
                    typos, never for meaning.
                </p>
                <StoryBodyEditor
                    alert={alert}
                    draft={draft}
                    onChange={onChange}
                />

                <FaqOfferFields
                    alert={alert}
                    researchOn={researchOn}
                    story={story}
                    draft={draft}
                    onChange={onChange}
                    onStoryChange={onSponsorChange}
                />
            </div>

            <aside className={styles.editorSide}>
                <SuggestedLink alert={alert} />
                <SeoPanel draft={draft} slug={slug} onChange={onChange} />
                <StorySponsorFields
                    draft={draft}
                    onChange={onChange}
                    researchOn={researchOn}
                />
                <DirectoryTopicsPicker
                    alertId={alert.id}
                    published={published}
                />
            </aside>
        </div>

        <div className={styles.editorActions}>
            <Button
                title={published ? 'Update live story' : 'Save draft'}
                theme={'success'}
                loading={saving}
                disabled={!dirty}
                onClick={onSave}
            />
            <Button theme={'secondary'} title={'Close'} onClick={onClose} />
            {dirty && <span className={styles.unsaved}>Unsaved changes</span>}
        </div>
        {error && (
            <p className={styles.error} role="alert">
                {error}
            </p>
        )}
    </section>
);

export default StoryEditor;
